import { API_BASE_URL } from "../config/api";

export class ApiError extends Error {
  constructor(message, { status, body, errors } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.errors = errors ?? null;
  }
}

/**
 * Prefer Laravel field `errors` (e.g. email already taken) over generic `reason`.
 */
export function formatApiErrorMessage(envelope, fallback = "Request failed.") {
  const fieldErrors = envelope?.errors;
  if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
    const messages = Object.values(fieldErrors)
      .flat()
      .map((msg) => String(msg ?? "").trim())
      .filter(Boolean);
    if (messages.length) {
      return [...new Set(messages)].join(" ");
    }
  }

  const reason = String(envelope?.reason ?? "").trim();
  if (
    reason &&
    !/^something went wrong\.?$/i.test(reason) &&
    !/^oops something went wrong/i.test(reason)
  ) {
    return reason;
  }

  return envelope?.message || fallback;
}

function throwFromEnvelope(envelope, json, status) {
  throw new ApiError(formatApiErrorMessage(envelope, status ? `Request failed (${status}).` : "Request failed."), {
    status,
    body: json,
    errors: envelope?.errors ?? null,
  });
}

export function unwrapApiEnvelope(json) {
  const envelope = json?.data ?? json;

  if (envelope?.in_error) {
    throwFromEnvelope(envelope, json);
  }

  const code = envelope?.status_code;
  if (code && code !== "000" && code !== "001" && envelope?.in_error !== false) {
    throwFromEnvelope(envelope, json);
  }

  return envelope?.data ?? envelope;
}

export async function apiRequest(path, { method = "GET", body, token, headers = {} } = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE_URL}${path}`;
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  const requestHeaders = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  if (!isFormData && requestHeaders["Content-Type"] == null) {
    requestHeaders["Content-Type"] = "application/json";
  }

  if (isFormData) {
    delete requestHeaders["Content-Type"];
  }

  const response = await fetch(url, {
    method,
    headers: requestHeaders,
    body: body == null ? undefined : isFormData ? body : JSON.stringify(body),
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    const envelope = json?.data ?? json;
    throwFromEnvelope(envelope, json, response.status);
  }

  return json;
}
